/**
 * Nav active-link sync — keeps `aria-current` and the active styling on the
 * correct nav link across Swup navigations, and, for links into SECTIONS of
 * the current page, only while that section is on screen.
 *
 * The site header/nav renders OUTSIDE `#swup`, so its server-rendered active
 * state (set via `{{ if is_current }}` in the nav partials) freezes on the first
 * page and points at the wrong link after every soft navigation. This re-derives
 * the active link from the URL on the canonical psd:page-view signal.
 *
 * Server-first: the correct link ships active in the initial HTML; this only
 * corrects it after client-side navigations. Markup contract (per nav link):
 *   - `data-nav-link`            — opt this link into the sync
 *   - `data-nav-active`   (opt)  — space-separated classes present ONLY when current
 *   - `data-nav-inactive` (opt)  — space-separated classes present ONLY when NOT current
 * The class lists live in the template so this module never hard-codes a design
 * (desktop nav and the mobile drawer use different active styles).
 *
 * ── TWO KINDS OF CURRENT (3 Sep 2026) ────────────────────────────────────────
 * The menu's home column lists the home page and deep links into two of its
 * sections (`/#what`, `/#approach`). Matched on pathname alone, all three were
 * current on `/` — three markers in one column. So:
 *
 *   - A link with NO hash is `aria-current="page"` when its path is the page's.
 *   - A link WITH a hash is never page-current. It is `aria-current="location"`
 *     only while the section it names is on screen, from an IntersectionObserver
 *     over the targets — the same shape as components/anchor-nav.js, rebuilt on
 *     every page view because Swup replaces the sections with the page. When no
 *     section is in view (the reader is in the hero, or past the last one) no
 *     section link is current, and the page link — Home — is the one marked.
 *
 * The CSS (`_chrome.css`, `.site-menu__link[aria-current]`) styles both values
 * alike and quiets the page link while a location link holds the marker.
 */

import { PAGE_VIEW_EVENT } from '../router/swup.js';

function classList(link, key) {
    return (link.dataset[key] || '').split(/\s+/).filter(Boolean);
}

function parse(link) {
    try {
        return new URL(link.href);
    } catch {
        return null;
    }
}

function isCurrent(link, pathname) {
    const url = parse(link);

    return url !== null && url.hash === '' && url.pathname === pathname;
}

function isSectionLink(link, pathname) {
    const url = parse(link);

    return url !== null && url.hash.length > 1 && url.pathname === pathname;
}

/**
 * Re-sync every `[data-nav-link]` to the current location. Idempotent — safe to
 * run on load and on every page:view. Leaves `aria-current="location"` alone:
 * the section spy below owns that value.
 *
 * @returns {void}
 */
export function syncNavActive() {
    const pathname = location.pathname;

    for (const link of document.querySelectorAll('[data-nav-link]')) {
        const active = classList(link, 'navActive');
        const inactive = classList(link, 'navInactive');

        if (isCurrent(link, pathname)) {
            link.setAttribute('aria-current', 'page');
            link.classList.add(...active);
            link.classList.remove(...inactive);
        } else {
            if (link.getAttribute('aria-current') === 'page') {
                link.removeAttribute('aria-current');
            }
            link.classList.remove(...active);
            link.classList.add(...inactive);
        }
    }
}

/* ── THE SECTION SPY ──────────────────────────────────────────────────────── */

let observer = null;

/** @type {Array<{ link: HTMLAnchorElement, section: Element }>} in document order */
let watched = [];

/** @type {Map<string, boolean>} section id → intersecting */
const visible = new Map();

function documentOrder(a, b) {
    const position = a.section.compareDocumentPosition(b.section);

    return position & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
}

function markLocation(activeId) {
    for (const { link, section } of watched) {
        if (section.id === activeId) {
            link.setAttribute('aria-current', 'location');
        } else if (link.getAttribute('aria-current') === 'location') {
            link.removeAttribute('aria-current');
        }
    }
}

/* The LAST intersecting section in document order wins, so a reader between two
   sections is placed in the one they have scrolled into rather than the one
   they are leaving. `null` when none is in view — no section link is current. */
function resolveLocation() {
    let winner = null;

    for (const { section } of watched) {
        if (visible.get(section.id)) {
            winner = section.id;
        }
    }

    markLocation(winner);
}

/**
 * Rebuild the section spy for the current page: find every `[data-nav-link]`
 * that names a section of THIS page, and watch those sections. Idempotent —
 * tears down the previous observer first, so it is safe on every page view.
 *
 * @returns {void}
 */
export function syncSectionLinks() {
    if (observer !== null) {
        observer.disconnect();
        observer = null;
    }

    markLocation(null);
    visible.clear();
    watched = [];

    const pathname = location.pathname;

    for (const link of document.querySelectorAll('[data-nav-link]')) {
        if (!isSectionLink(link, pathname)) {
            continue;
        }

        const section = document.getElementById(decodeURIComponent(parse(link).hash.slice(1)));

        if (section !== null) {
            watched.push({ link, section });
        }
    }

    if (watched.length === 0 || !('IntersectionObserver' in window)) {
        return;
    }

    watched.sort(documentOrder);

    /* A section counts as on screen once it reaches the top 30% of the
       viewport — the band a reader's eye is actually in — and stops counting
       when it has scrolled out above. Same margin as anchor-nav.js. */
    observer = new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                visible.set(entry.target.id, entry.isIntersecting);
            }
            resolveLocation();
        },
        { rootMargin: '0px 0px -70% 0px', threshold: 0 },
    );

    for (const { section } of watched) {
        observer.observe(section);
    }
}

function sync() {
    syncNavActive();
    syncSectionLinks();
}

/**
 * Wire the sync: run once now, then re-run after every Swup navigation settles.
 *
 * @returns {void}
 */
export function initNavActiveSync() {
    sync();
    window.addEventListener(PAGE_VIEW_EVENT, sync);
}
